import { and, equal, not, reads, readOnly } from '@ember/object/computed';
import { computed } from '@ember/object';
import DS from 'ember-data';
import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { belongsTo, hasMany } from 'ember-data/relationships';
import RouterService from '@ember/routing/router-service';
import { DAYS, daysToSet } from 'cio/utils/delivery-window';
import colorLabels from 'cio/utils/color-labels';
import actionInfo, { COMPOSABLE_TYPES, MESSAGING_TYPES, EFFECT_TYPES } from 'cio/utils/action-info';
import { dropTask } from 'ember-concurrency';
import { modelApiAction } from 'cio/utils/model-api-actions';
import { isEmpty, isNone } from '@ember/utils';
import { uniq } from 'cio/utils/array';
import { MAX_RANDOM_COHORT_ALLOCATION } from 'cio/utils/visual-workflow-builder';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import CampaignModel from 'cio/models/campaign';
import CollectionModel from 'cio/models/collection';
import SegmentModel from 'cio/models/segment';
import TemplateModel from 'cio/models/template';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import { IdentifierKey } from 'cio/utils/identifiers';
import AnalyticsService from 'cio/services/analytics';
import { CREATED_IN_CIO_AT } from 'cio/utils/attributes';
import { LOCAL_TIME_ZONE } from 'cio/utils/time-zones';
import SubscriptionCenterService from 'cio/services/subscription-center';
import isDestroyed from 'cio/utils/is-destroyed';

export const BRANCH_TYPES = [
  'conditional_branch_action',
  'multi_split_branch_action',
  'random_cohort_branch_action',
  'conditional_wait_action',
];

export const DELAY_TYPES = ['delay_seconds_action', 'delay_time_window_action', 'conditional_wait_action'];
const DEFAULT_DAYS_VALUE = DAYS.join(':');

export type ActionType =
  | 'conditional_wait_action'
  | 'delay_seconds_action'
  | 'delay_time_window_action'
  | 'grace_period_action'
  | 'email_action'
  | 'slack_action'
  | 'twilio_action'
  | 'push_action'
  | 'in_app_action'
  | 'urban_airship_action'
  | 'webhook_action'
  | 'attribute_update_action'
  | 'static_seg_update_action'
  | 'collection_query_action'
  | 'create_event_action'
  | 'batch_update_action'
  | 'split_randomized_action'
  | 'conditional_branch_action'
  | 'multi_split_branch_action'
  | 'multi_lang_branch_action'
  | 'random_cohort_branch_action'
  | 'filter_match_delay_action'
  | 'exit_action';

export type ActionTypeCondition =
  | 'isAttributeAction'
  | 'isManualSegmentUpdateAction'
  | 'isCollectionAction'
  | 'isEmail'
  | 'isWaitUntil'
  | 'isTimeDelay'
  | 'isTimeWindow'
  | 'isExit'
  | 'isConditionalBranch'
  | 'isMultiSplitBranch'
  | 'isRandomCohortBranch'
  | 'isSplitTest';

export enum SplitState {
  Inactive = 'inactive',
  Paused = 'paused',
  Running = 'running',
}

export type VariationBadge = {
  label: string;
  color: string;
  allocation: number;
  active: boolean;
  paused: boolean;
};

export enum ActionState {
  Paused = 'paused',
  Running = 'running',
  Deleting = 'deleting',
  Evaluating = 'evaluating',
}

export enum SendingState {
  Automatic = 'automatic',
  Draft = 'draft',
  Off = 'off',
}

export enum Platform {
  All = 'all',
  iOS = 'ios',
  Android = 'android',
}

export enum PlatformProvider {
  APNS = 'apns',
  FCM = 'fcm',
}

export const TARGET_PLATFORMS = [
  { label: 'All', value: Platform.All },
  { label: 'iOS', value: Platform.iOS },
  { label: 'Android', value: Platform.Android },
];

export enum ExitType {
  Continue = 'continue',
  // XXX: Deprecated
  Leave = 'leave',
  Never = 'never',
}

export default class ActionModel extends Model {
  @service analytics!: AnalyticsService;
  @service router!: RouterService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;

  @attr('string') type!: ActionType;
  @attr('number') created!: number;
  @attr('number') updated!: number;
  @attr('string') actionState!: ActionState;
  @attr('base64-json', { defaultValue: () => [] }) preconditions!: SerializedLegacyFilter[];

  @belongsTo('campaign', { async: false, inverse: 'actions' }) campaign!: CampaignModel;
  @attr('string') warnings!: string;

  @attr('boolean', { allowNull: true, defaultValue: false }) holdout!: boolean;

  // This isn't sent to the backend - it's an internal attribute to track action
  // models that are only temporarily created for copying purposes in the VWB
  @attr('boolean') _copy!: boolean;

  // will be present if action is part of a split action
  @belongsTo('action', { async: true, inverse: 'actions' }) parentAction!: ActionModel;

  get isVariation() {
    return !isNone((this as ActionModel).belongsTo('parentAction').id());
  }

  // type = 'delay_seconds_action', type = 'conditional_wait_action'
  @attr('number', { defaultValue: 0 }) delay!: number;
  @equal('type', 'delay_seconds_action') isTimeDelay!: boolean;

  // type = 'conditional_wait_action'
  @attr('base64-json', { defaultValue: () => [] }) conditions!: SerializedLegacyFilter[];
  @attr('string', { defaultValue: ExitType.Never }) exitType!: ExitType;
  @equal('type', 'conditional_wait_action') isWaitUntil!: boolean;
  @attr('boolean', { defaultValue: false }) isPreBranchWaitUntil!: boolean;

  @attr('string') delayEventAttr!: string;
  @attr('number', { defaultValue: 0 }) delayEventAttrOffset!: number;

  // type = 'delay_time_window_action'
  @attr('string', { defaultValue: '0:00' }) startTime!: string;
  @attr('string', { defaultValue: '23:59' }) endTime!: string;
  @attr('string', { defaultValue: DEFAULT_DAYS_VALUE }) days!: string;
  @attr('string', {
    defaultValue() {
      return LOCAL_TIME_ZONE.value;
    },
  })
  zone!: string;
  @attr('string') fallbackZone!: string;
  @equal('type', 'delay_time_window_action') isTimeWindow!: boolean;

  // these get copied from the template and apply to many action types
  @attr('string') recipient!: string;
  @attr('string') subject!: string;

  // type = 'email_action'
  @attr('string') name!: string;
  @belongsTo('template', { async: true, inverse: 'action' }) template!: DS.PromiseObject<TemplateModel>;
  @attr('string', { defaultValue: SendingState.Draft }) sendingState!: SendingState;
  @attr('boolean', { defaultValue: true }) tracked!: boolean;
  @attr('boolean', { allowNull: true }) sendToUnsubscribed!: boolean;
  @attr('number') subscriptionTopicId!: number;

  @attr('boolean', { allowNull: true }) useMessageLimits!: boolean;
  @attr('boolean') overrideMessageLimitsRetry!: boolean;
  @attr('number') messageLimitsRetryWindowSecs!: number;

  // type = 'webhook_action'
  @attr('string') requestMethod!: string;
  @attr('string') hostname!: string;
  @attr('boolean') hasUrl!: boolean;
  @attr('array') attributesNames!: string[];
  @attr('array') attributesValues!: string[];
  @attr('boolean', { allowNull: true }) canConvert!: boolean;

  // type = 'create_event_action'
  @equal('type', 'create_event_action') isCreateEventAction!: boolean;
  @attr('string') eventName!: string;

  @computed('attributesNames.[]', 'attributesValues.[]')
  get hasAttributeUpdates() {
    return (this.attributesNames ?? []).length > 0 && (this.attributesValues ?? []).length > 0;
  }

  // type = 'batch_update'
  @equal('type', 'batch_update_action') isBatchUpdateAction!: boolean;
  @attr('string') templateType!: string; // copied from template models

  // type = 'split_randomized_action'
  @hasMany('action', { async: false, inverse: 'parentAction' }) actions!: ActionModel[];
  @attr() allocations!: number[]; // JSON array
  @attr('string', { defaultValue: SplitState.Inactive }) splitState!: SplitState;
  @attr('number') started!: number;

  // type = 'attribute_update_action'
  @attr('string') attributeName!: string;
  @attr('string') attributeValue!: string;
  @attr('string') attributes!: string;
  @equal('type', 'attribute_update_action') isAttributeAction!: boolean;
  get isCreateOrUpdatePersonAttributeAction() {
    // See https://github.com/customerio/issues/issues/10162 and the
    // corresponding PR.
    // There seems to be a bug with ember data 4.4 (at least) where ember data
    // is trashing the model reference, causing an update and this getter to
    // re-evaluate. Because the referenced model of the belongsTo doesn't exist
    // anymore, ember throws an error we aren't catching, causing the UI to
    // be blocked. As far as we can tell, this only happens when this model is
    // being destroyed, so we add this condition to avoid the `belongsTo` call.
    //
    // We can approximate it roughly by checking for `templateId`, which will
    // normally be populated except during the workflow copy process, where a
    // template with `copy_<id>` won't be found and `templateId` won't be
    // populated by ember when this model is inserted into the store.
    if (this.isDestroyed || this.isDestroying) {
      return this.isAttributeAction && this.templateId != null;
    }

    return this.isAttributeAction && this.belongsTo('template' as any).id() != null;
  }

  // type = 'static_seg_update_action'
  @belongsTo('segment', { async: true, inverse: null }) segment!: SegmentModel;
  @attr('boolean', { allowNull: true }) addToSeg!: boolean;
  @equal('type', 'static_seg_update_action') isManualSegmentUpdateAction!: boolean;

  // type = 'collection_query_action'
  @attr('boolean', { defaultValue: false }) asc!: boolean;
  @attr('number', { defaultValue: 10 }) limit!: number;
  @attr('boolean', { defaultValue: false }) randomize!: boolean;
  @attr('boolean', { defaultValue: false }) singleResult!: boolean;
  @attr('string') orderBy!: string;
  @attr('array', { defaultValue: () => [] }) queryPreviewColumns!: string[]; // JSON array
  @belongsTo('collection', { async: true, inverse: null }) collection!: CollectionModel;
  @equal('type', 'collection_query_action') isCollectionAction!: boolean;
  @computed('asc', {
    get() {
      return !this.asc;
    },
    set(_key, value) {
      this.set('asc', !value);
      return value;
    },
  })
  desc!: boolean; // sort order for collection query items

  // type = 'push_action'
  @attr('string') sendToPlatform!: string;
  @equal('sendToPlatform', Platform.All) isPlatformDefault!: boolean;
  @not('isPlatformDefault') isPlatformNotDefault!: boolean;
  @and('sendToPlatform', 'isPlatformNotDefault') isPlatformSelected!: boolean;

  // type = 'multi_split_branch_action', type = 'multi_lang_branch_action'
  // Multiconditions is an array of arrays: [ filter, filter, filter ]
  // Where filter is an array of conditions [ { type: 'attribute', ... }, {
  // type: 'segments_negatable', ... } ]
  // All filters in a multiconditions array share the type, and each filter
  // will only have one condition.
  // Note: the multi_lang_branch_action branch conditions may not accurately reflect the
  // workspace language attribute used, since the server pulls the workspace language attribute
  // value dynamically when processing (and campaigns are not updated when the language attribute
  // is changed by the user)
  @attr('multi-conditions', { defaultValue: () => [] }) multiConditions!: SerializedLegacyFilter[][];

  // type = 'random_cohort_branch_action'
  // cohorts is an array of integers.
  // Each integer should range from 0 to 1000. If the cohorts aren't "evenly
  // split", they should sum up to 1000.
  // If, during editing a random branch, the customer chooses "distribute
  // evenly", then all cohorts are set to 1.
  // We do this because we can't accurately represent all percentages as an
  // integer (see below comment on fractions), so cohorts aren't really
  // percentages - they're weights.
  @attr() cohorts!: number[]; // JSON array

  @computed('cohorts.[]')
  get isDistributedEvenly() {
    // Cohorts don't need to add up to 100%, and in order to avoid the problem of
    // trying to represent fractions as numbers (100/3 = 3.33333..) we simply
    // set them all to 1 - indicating that all cohorts are equal.
    // It's also possible for some cohorts to have a value of 0, which is what
    // we set new cohort values to. We still treat this as "even distribution"
    // for percentage calculation purposes.
    // We count distributions as even also when all cohorts have the same value.
    let isAutomaticDistribution = this.cohorts.every((c: number) => c === 1 || c === 0);

    if (isAutomaticDistribution) {
      return true;
    }

    let isDistributedEvenlyManually = this.cohorts.every((c: number) => c === this.cohorts[0]);
    let sumsToMax = this.cohorts.reduce((acc: number, c: number) => acc + c, 0) === MAX_RANDOM_COHORT_ALLOCATION;
    return isDistributedEvenlyManually && sumsToMax;
  }

  @computed('cohorts.[]', 'isDistributedEvenly')
  get evenDistributionValue() {
    if (!this.isDistributedEvenly) {
      return;
    }

    let isDistributedEvenlyManually = this.cohorts.every(
      (cohort: number) => cohort !== 1 && cohort === this.cohorts[0],
    );

    if (isDistributedEvenlyManually) {
      return this.cohorts[0];
    }

    let distributedPathCount = this.cohorts.filter((cohort: number) => cohort === 1).length;
    return Math.floor(MAX_RANDOM_COHORT_ALLOCATION / distributedPathCount);
  }

  @computed('multiConditions.[]')
  get selectedMultiCondition() {
    if (this.multiConditions && this.multiConditions.length > 0) {
      return this.multiConditions[0][0];
    }

    return null;
  }

  // type = 'multi_split_branch_action', type = 'multi_lang_branch_action', type = 'conditional_branch_action'
  @computed('isConditionalBranch', 'isMultiSplitBranch', 'isMultiLangBranch')
  get branchNeedsCondition() {
    return this.isConditionalBranch || this.isMultiSplitBranch || this.isMultiLangBranch;
  }

  @computed('type', 'conditions', 'multiConditions')
  get branchHasCondition() {
    if (!this.isBranch) {
      return false;
    }

    if (this.type === 'multi_split_branch_action' || this.type === 'multi_lang_branch_action') {
      return !isEmpty(this.multiConditions);
    }

    if (this.type === 'conditional_branch_action') {
      return !isEmpty(this.conditions);
    }

    return false;
  }

  // Templated action computeds and associations
  @attr('number') templateId!: number;
  @attr('boolean') hasContent!: boolean;
  @attr('boolean') hasSubject!: boolean;
  @not('hasSubject') hasNoSubject!: boolean;
  @not('hasContent') hasNoContent!: boolean;
  @not('hasUrl') hasNoRequest!: boolean;

  // Distinguish between delay and messaging action types
  @computed('type')
  get isDelay() {
    return DELAY_TYPES.includes(this.type);
  }

  @computed('type')
  get isMessagingType() {
    return MESSAGING_TYPES.includes(this.type);
  }

  @computed('type')
  get isComposable() {
    return COMPOSABLE_TYPES.includes(this.type);
  }

  @computed('type')
  get isEffect() {
    return EFFECT_TYPES.includes(this.type);
  }

  @computed('type')
  get hasEmailStats() {
    return this.type === 'email_action' || this.type === 'split_randomized_action';
  }

  @equal('type', 'email_action') isEmail!: boolean;
  @equal('type', 'in_app_action') isInApp!: boolean;

  @equal('type', 'exit_action') isExit!: boolean;
  @equal('type', 'conditional_branch_action') isConditionalBranch!: boolean;
  @equal('type', 'multi_split_branch_action') isMultiSplitBranch!: boolean;
  @equal('type', 'multi_lang_branch_action') isMultiLangBranch!: boolean;
  @equal('type', 'random_cohort_branch_action') isRandomCohortBranch!: boolean;
  @computed(
    'isConditionalBranch',
    'isMultiSplitBranch',
    'isRandomCohortBranch',
    'isMultiLangBranch',
    'isWaitUntil',
    'isPreBranchWaitUntil',
  )
  get isBranch(): boolean {
    if (this.isPreBranchWaitUntil) {
      return false;
    }

    return (
      this.isConditionalBranch ||
      this.isMultiSplitBranch ||
      this.isRandomCohortBranch ||
      this.isMultiLangBranch ||
      this.isWaitUntil
    );
  }

  @computed('id', 'campaign.{edges,branchTree}')
  get subActions(): string[] {
    let parsedBranch = this.campaign.branchesMap[this.id];
    let subActionIds = parsedBranch.edges.reduce((acc, parsedEdge) => {
      acc.push(...(parsedEdge.allEdgeNodes ?? []).map((actionModel) => actionModel.id));
      return acc;
    }, [] as string[]);

    return uniq(subActionIds);
  }

  // Various type-dependent information about the action
  @computed('type', 'canConvert')
  get info() {
    return actionInfo(this.type, { canConvert: this.canConvert });
  }

  // Human-readable text for the action type
  @reads('info.name') typeName!: string;

  // Action FSM computeds
  @equal('actionState', ActionState.Paused) isPaused!: boolean;
  @equal('actionState', ActionState.Running) isRunning!: boolean;
  @equal('actionState', ActionState.Deleting) isDeleting!: boolean;
  @equal('actionState', ActionState.Evaluating) isEvaluating!: boolean;

  // Split FSM computeds and manipulation
  @equal('type', 'split_randomized_action') isSplitTest!: boolean;
  @equal('splitState', SplitState.Inactive) isSplitInactive!: boolean;
  @equal('splitState', SplitState.Paused) isSplitPaused!: boolean;
  @equal('splitState', SplitState.Running) isSplitRunning!: boolean;

  // For now, a split test's name is aliased to the name of the original.
  // In future, we may want to use the test's own name field.
  @readOnly('actions.firstObject.name') splitTestName!: string;

  startTest() {
    this.set('splitState', SplitState.Running);
    return this.save();
  }

  pauseTest() {
    this.set('splitState', SplitState.Paused);
    return this.save();
  }

  // Window action computeds
  @computed('days')
  get anyDay() {
    let days = daysToSet(this.days);
    return DAYS.every((day) => days[day]);
  }

  @computed('startTime', 'endTime')
  get anyTime() {
    return this.startTime === '0:00' && this.endTime === '23:59';
  }

  // Email action sending state computeds
  @equal('sendingState', SendingState.Automatic) isAutomatic!: boolean;
  @equal('sendingState', SendingState.Draft) isDraft!: boolean;
  @equal('sendingState', SendingState.Off) isOff!: boolean;

  // Split action computeds
  @computed('isSplitTest', 'isSplitRunning', 'actions.[]', 'allocations.[]')
  get splitActions() {
    // isDestroyed check needed in Ember 4.12
    // See tests: http://127.0.0.1:4200/tests?testId=a0aa3f15&testId=6aa39693&testId=8e238330&testId=46634cf6&testId=ed946870
    if (!this.isSplitTest || isDestroyed(this)) {
      return [];
    }

    let { isSplitRunning, allocations } = this;

    let splits = (this as ActionModel).hasMany('actions').value()?.toArray() ?? [];

    return splits.map((action, index) => {
      let { label, color } = colorLabels[index];
      let badge: VariationBadge = {
        label,
        color,
        allocation: allocations[index] as number,
        active: !isSplitRunning && index === 0,
        paused: !isSplitRunning && index !== 0,
      };

      return { action, badge };
    });
  }

  @computed('sendToUnsubscribed', 'campaign.sendToUnsubscribed')
  get willSendToUnsubscribed() {
    return this.sendToUnsubscribed ?? this.campaign.sendToUnsubscribed;
  }

  @computed('warnings')
  get warningsArray() {
    if (!this.warnings?.trim()) {
      return [];
    }

    return this.warnings.split(',');
  }

  // Prevent the same analytics event from firing when errors are re-computed
  trackedErrors: string[] = [];

  @computed('type', 'attributeName', 'trackedErrors')
  get errorsMap() {
    let errors: Partial<Record<keyof ActionModel, string[]>> = {};

    if (this.type === 'attribute_update_action' || this.type === 'collection_query_action') {
      if ([IdentifierKey.CioId, CREATED_IN_CIO_AT].includes(this.attributeName)) {
        let invalidKeyError = `You cannot use ${this.attributeName}.`;
        errors.attributeName = [invalidKeyError];

        if (!this.trackedErrors.includes(invalidKeyError)) {
          this.set('trackedErrors', [...this.trackedErrors, invalidKeyError]);

          this.analytics.track('user', 'error_encountered', {
            currentUrl: this.router.currentURL,
            message: invalidKeyError,
            source: this.type === 'attribute_update_action' ? 'Attribute Update Action' : 'Collection Query Action',
          });
        }
      }
    }

    return errors;
  }

  get subscriptionTopic() {
    if (this.subscriptionTopicId == null || !this.subscriptionCenter.initialized) {
      return null;
    }

    return this.subscriptionCenter.topics.findBy('id', this.subscriptionTopicId.toString());
  }

  get hasDeletedSubscriptionTopic() {
    return !isEmpty(this.subscriptionTopicId) && this.subscriptionTopic == null;
  }

  addLanguageConditionsToMultiLanguageBranchAction(languages: string[]) {
    if (!this.isMultiLangBranch) {
      throw new Error('Cannot add language conditions to a non-multi-language branch action');
    }

    let { multiConditions } = this;
    for (let language of languages) {
      let newCondition = [
        { type: 'attribute', field: this.environment.current.languageAttribute, operator: 'eq', value: language },
      ];
      multiConditions = [newCondition, ...multiConditions];
    }
    this.set('multiConditions', multiConditions);
  }

  dismissWarningTask = dropTask(async (warning: string) => {
    this.set('warnings', this.warningsArray.filter((w) => w !== warning).join(','));

    try {
      await this.save();
    } catch (errors) {
      this.rollbackAttributes();
      throw errors;
    }
  });

  createSplit = modelApiAction({
    path: 'test',
    type: 'POST',
    before(this: ActionModel) {
      return {
        campaign_id: this.belongsTo('campaign').id(),
        type: 'randomized',
      };
    },

    after(this: ActionModel, response) {
      let responseWithoutMeta = { ...response };
      delete responseWithoutMeta.meta;

      this.store.pushPayload(responseWithoutMeta);
      return response;
    },
  });

  selectSplitWinner = modelApiAction({
    path: 'winner',
    type: 'POST',
    before(this: ActionModel, data: { campaignId: string; winnerId: string }) {
      return {
        campaign_id: data.campaignId,
        winner_id: data.winnerId,
      };
    },

    async after(this: ActionModel, response) {
      let responseWithoutMeta = { ...response };
      delete responseWithoutMeta.meta;

      this.store.pushPayload(responseWithoutMeta);
      await this.belongsTo('template').reload();
      return response;
    },
  });

  // Delete using API call instead of destroy record, because deleting an action
  // isn't instant. The payload still returns the action in a "deleting" state.
  // While it's in this state, we may poll the API (in a component) and only
  // remove the action from the store once it's really gone.
  deleteViaAPI = modelApiAction({
    type: 'DELETE',
    path: '',
    after(this: ActionModel, response) {
      this.store.pushPayload(response);
      return response;
    },
  });

  fetchPreviewVariables = modelApiAction({
    type: 'GET',
    path: 'variables',
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    action: ActionModel;
  }
}
